import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { animated, useSpring } from '@react-spring/web';
import { useWindowWidth } from '@wojtekmaj/react-hooks';
import T from '@wojtekmaj/react-t';
import {
  HomeIcon,
  MoreHorizontalIcon,
  SaladIcon,
  ScanBarcodeIcon,
  ShoppingBasketIcon,
} from 'lucide-react';

import {
  Icon,
  Label,
  List,
  ListItem,
  ListItemLink,
  ListItemUnderlay,
  TabIndicator,
  Wrapper,
} from './menu.styles';

import type { LucideIcon } from 'lucide-react';

const AnimatedTabIndicator = animated(TabIndicator);

type Item = {
  label: string;
  icon: LucideIcon;
  href: string;
  exact?: boolean;
  primary?: boolean;
};

type ItemWithFlags = Item & { active: boolean };

function areRectsEqual(prevRect: DOMRect | null, newRect: DOMRect): boolean {
  return Boolean(prevRect && prevRect.x === newRect.x && prevRect.width === newRect.width);
}

const items = [
  {
    label: 'Home',
    icon: HomeIcon,
    href: '/',
    exact: true,
  },
  {
    label: 'Menu',
    icon: SaladIcon,
    href: '/products',
  },
  {
    label: 'My card',
    icon: ScanBarcodeIcon,
    href: '#your-code',
    primary: true,
  },
  {
    label: 'Order and collect',
    icon: ShoppingBasketIcon,
    href: '/order_and_collect',
  },
  {
    label: 'More',
    icon: MoreHorizontalIcon,
    href: '/more',
  },
] satisfies Item[];

type MenuItemProps = {
  item: ItemWithFlags;
  onActive: (layout: DOMRect, parentLayout: DOMRect) => void;
};

function MenuItem({ item, onActive }: MenuItemProps) {
  const [domRect, setDomRect] = useState<DOMRect | null>(null);
  const [parentDomRect, setParentDomRect] = useState<DOMRect | null>(null);

  const { label, icon: IconComponent, href, exact, primary, active } = item;

  useEffect(() => {
    if (!active || !domRect || !parentDomRect) {
      return;
    }

    onActive(domRect, parentDomRect);
  }, [active, domRect, onActive, parentDomRect]);

  return (
    <ListItem
      ref={(ref) => {
        if (!ref) {
          return;
        }

        function getRect() {
          if (!ref) {
            return;
          }

          const parentElement = ref.parentElement;

          if (!parentElement) {
            return;
          }

          const rect = ref.getBoundingClientRect();
          const parentRect = parentElement.getBoundingClientRect();

          setDomRect((prevDomRect) => (areRectsEqual(prevDomRect, rect) ? prevDomRect : rect));

          setParentDomRect((prevParentRect) =>
            areRectsEqual(prevParentRect, parentRect) ? prevParentRect : parentRect,
          );
        }

        getRect();

        document.fonts.addEventListener('loadingdone', getRect);
      }}
    >
      <ListItemLink
        active={active}
        primary={primary}
        state={primary ? { scrollToTop: false } : undefined}
        to={href}
        end={exact}
      >
        <ListItemUnderlay />
        <Icon active={active} primary={primary}>
          <IconComponent />
        </Icon>
        <Label>
          <T>{label}</T>
        </Label>
      </ListItemLink>
    </ListItem>
  );
}

export default function Menu() {
  const windowWidth = useWindowWidth();
  const location = useLocation();
  const shouldUseImmediate = useRef(true);
  const [indicatorRect, setIndicatorRect] = useState<object>({ left: 0, opacity: 1 });

  const props = useSpring(indicatorRect);

  const itemsWithFlags = useMemo<ItemWithFlags[]>(
    () =>
      items.map((item) => {
        if (item.href.startsWith('#')) {
          return {
            ...item,
            active: location.hash === item.href,
          };
        }

        if (item.exact) {
          return {
            ...item,
            active: location.pathname === item.href && !location.hash,
          };
        }

        const pattern = `${item.href}/*`;
        const match = matchPath({ path: pattern }, location.pathname);

        return {
          ...item,
          active: Boolean(match) && !location.hash,
        };
      }),
    [location.hash, location.pathname],
  );

  const activeItem = itemsWithFlags.find((item) => item.active);
  const hasActiveItem = Boolean(activeItem);

  useEffect(() => {
    if (hasActiveItem) {
      return;
    }

    setIndicatorRect({
      opacity: 0,
      immediate: true,
    });

    shouldUseImmediate.current = true;
  }, [hasActiveItem]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect intentionally triggered on window width change
  useEffect(() => {
    shouldUseImmediate.current = true;
  }, [windowWidth]);

  const onActive = useCallback((rect: DOMRect, parentRect: DOMRect) => {
    const immediate = shouldUseImmediate.current;

    setIndicatorRect({
      left: rect.x - parentRect.x + rect.width / 2,
      opacity: 1,
      immediate,
    });

    shouldUseImmediate.current = false;
  }, []);

  return (
    <Wrapper>
      <List itemCount={itemsWithFlags.length} role="list">
        {itemsWithFlags.map((item) => (
          <MenuItem key={item.href} item={item} onActive={onActive} />
        ))}
      </List>
      <AnimatedTabIndicator style={props} />
    </Wrapper>
  );
}
